import {Component, Input, OnInit} from '@angular/core';
import {TarefaService} from "../../../services/tarefa-service";
import {ToastrService} from "ngx-toastr";
import {ItemHora, TarefasAgrupadasPorHora} from "../../../models/tarefas-agrupadas-por-hora";
import {ItemTarefa} from "../../../models/lista-tarefa";

interface EventItem {
  title: string;
  start: string;
  end: string;
  id?: number;
  column?: number;
  totalColumns?: number;
}

@Component({
    selector: 'app-diario',
    templateUrl: './diario.component.html',
    styleUrl: './diario.component.css',
    standalone: false
})
export class DiarioComponent implements OnInit {
  events: EventItem[] = [];
  horas: string[] = [];
  currentTimeTop = 0;
  today = new Date();
  eventColors: string[] = ['#2E5266', '#2E5266', '#fd7e14', '#28a745', '#dc3545', '#6E8898'];

  @Input() telaPequena = false;
  loading = false;
  diaTexto = 0;
  diaAtual = new Date();
  mesTexto = '';
  anoTexto = 0;
  lista = new TarefasAgrupadasPorHora();

  ngOnInit() {
    this.horas = [];
    for (let i = 6; i < 23; i++) {
      this.horas.push(`${i.toString().padStart(2, '0')}:00`);
    }
    this.atualizarLinhaTempo(); // depois a cada minuto
    setInterval(() => {
      this.atualizarLinhaTempo(); // depois a cada minuto
      this.buscarCompromissosDiario(this.diaAtual);
    }, 60000);
  }

  atualizarLinhaTempo() {
    const agora = new Date();
    const minutosAgora = agora.getHours() * 60 + agora.getMinutes();
    const minutosBase = 6 * 60; // timeline começa às 06:00
    this.currentTimeTop = minutosAgora - minutosBase;
  }

  carregarTarefas(tarefasAgrupadas: TarefasAgrupadasPorHora) {
    const tempEvents: EventItem[] = [];
    let eventId = 0;

    tarefasAgrupadas.itemHoras.forEach((item: ItemHora) => {
      item.tarefas.forEach((tarefa: ItemTarefa) => {
        const dataInicio = new Date(tarefa.dataInicial);
        let dataFim = tarefa.dataPrevisaoFinalizacao
          ? new Date(tarefa.dataPrevisaoFinalizacao)
          : new Date(dataInicio.getTime() + 30 * 60000); // +30 min se não tiver previsão

        // Se for a mesma hora, adiciona 30 minutos
        if (dataInicio.getTime() === dataFim.getTime()) {
          dataFim = new Date(dataInicio.getTime() + 30 * 60000);
        }

        tempEvents.push({
          id: eventId++,
          title: tarefa.descricao,
          start: this.formatTime(dataInicio),
          end: this.formatTime(dataFim)
        });
      });
    });

    // Organizar eventos em colunas quando houver sobreposição
    this.events = this.organizarEventosEmColunas(tempEvents);
  }

  // Verifica se dois eventos se sobrepõem
  private eventosSesobrepoem(event1: EventItem, event2: EventItem): boolean {
    const start1 = this.timeToMinutes(event1.start);
    const end1 = this.timeToMinutes(event1.end);
    const start2 = this.timeToMinutes(event2.start);
    const end2 = this.timeToMinutes(event2.end);

    return (start1 < end2 && start2 < end1);
  }

  // Organiza eventos em colunas quando há sobreposição
  private organizarEventosEmColunas(events: EventItem[]): EventItem[] {
    if (events.length === 0) return [];

    // Ordenar eventos por hora de início
    const sortedEvents = [...events].sort((a, b) =>
      this.timeToMinutes(a.start) - this.timeToMinutes(b.start)
    );

    // Agrupar eventos que se sobrepõem
    const grupos: EventItem[][] = [];
    let grupoAtual: EventItem[] = [sortedEvents[0]];

    for (let i = 1; i < sortedEvents.length; i++) {
      const evento = sortedEvents[i];
      let sobrepoeComGrupoAtual = false;

      // Verifica se o evento atual se sobrepõe com qualquer evento do grupo atual
      for (const eventoGrupo of grupoAtual) {
        if (this.eventosSesobrepoem(evento, eventoGrupo)) {
          sobrepoeComGrupoAtual = true;
          break;
        }
      }

      if (sobrepoeComGrupoAtual) {
        // Adiciona ao grupo atual se houver sobreposição
        grupoAtual.push(evento);
      } else {
        // Finaliza o grupo atual e inicia um novo
        grupos.push([...grupoAtual]);
        grupoAtual = [evento];
      }
    }

    // Adiciona o último grupo
    grupos.push(grupoAtual);

    // Para cada grupo, atribui colunas aos eventos
    const resultEvents: EventItem[] = [];

    grupos.forEach(grupo => {
      // Se o grupo tem apenas um evento, não precisa de coluna
      if (grupo.length === 1) {
        resultEvents.push({...grupo[0], column: 0, totalColumns: 1});
        return;
      }

      // Atribui colunas para eventos sobrepostos
      const colunas: EventItem[][] = [];

      grupo.forEach(evento => {
        // Encontra a primeira coluna disponível
        let colunaIndex = 0;
        while (true) {
          if (!colunas[colunaIndex]) {
            colunas[colunaIndex] = [];
          }

          // Verifica se o evento pode ser colocado nesta coluna
          let podeColocar = true;
          for (const eventoColuna of colunas[colunaIndex]) {
            if (this.eventosSesobrepoem(evento, eventoColuna)) {
              podeColocar = false;
              break;
            }
          }

          if (podeColocar) {
            colunas[colunaIndex].push(evento);
            resultEvents.push({...evento, column: colunaIndex, totalColumns: colunas.length});
            break;
          }

          colunaIndex++;
        }
      });

      // Atualiza o totalColumns para todos os eventos deste grupo
      const totalColunas = colunas.length;
      resultEvents.forEach(e => {
        if (grupo.some(ge => ge.id === e.id)) {
          e.totalColumns = totalColunas;
        }
      });
    });

    return resultEvents;
  }

  formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  getEventStyle(event: EventItem) {
    const startMin = this.timeToMinutes(event.start);
    const endMin = this.timeToMinutes(event.end);
    const baseMin = 6 * 60; // início da timeline: 6h da manhã

    // Valores padrão para eventos sem coluna
    let left = '100px';
    let width = 'calc(100% - 110px)';
    let backgroundColor = this.eventColors[0]; // cor padrão

    // Se o evento tem coluna e totalColumns definidos, ajusta a posição e largura
    if (event.column !== undefined && event.totalColumns && event.totalColumns > 0) {
      // Calcula a largura de cada coluna (subtraindo o espaço para as labels de hora)
      const availableWidth = 'calc(100% - 110px)';

      // Adiciona um pequeno espaçamento entre as colunas (4px de cada lado)
      const columnSpacing = 8; // 4px de cada lado
      const columnWidth = `calc((${availableWidth}) / ${event.totalColumns})`;

      // A largura real do evento é um pouco menor que a largura da coluna para criar espaço entre eventos
      const eventWidth = `calc(${columnWidth} - ${columnSpacing}px)`;

      // Calcula a posição esquerda com base na coluna, adicionando metade do espaçamento
      left = `calc(100px + (${event.column} * ${columnWidth}) + ${columnSpacing / 2}px)`;

      // Define a largura como a largura do evento (menor que a coluna)
      width = eventWidth;

      // Escolhe uma cor com base na coluna (usando módulo para ciclar pelas cores disponíveis)
      backgroundColor = this.eventColors[event.column % this.eventColors.length];
    }

    return {
      top: `${startMin - baseMin}px`,
      height: `${endMin - startMin}px`,
      left: left,
      width: width,
      backgroundColor: backgroundColor
    };
  }

  timeToMinutes(time: string): number {
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  }


  constructor(private tarefaServico: TarefaService,
              private toastr: ToastrService) {
    this.buscarCompromissosDiario(new Date());
    this.setTextoData(this.diaAtual);

  }

  public adicionarDia(){
    this.loading = true;
    this.diaAtual.setDate(this.diaAtual.getDate() + 1);
    this.setTextoData(this.diaAtual);
    this.buscarCompromissosDiario(this.diaAtual);
  }

  public diminuirDia(){
    this.loading = true;
    this.diaAtual.setDate(this.diaAtual.getDate() - 1);
    this.setTextoData(this.diaAtual);
    this.buscarCompromissosDiario(this.diaAtual);
  }

  private setTextoData(dataAtual: Date){
    this.diaTexto = dataAtual.getDate();
    this.mesTexto = this.primeiraLetraMaiuscula(dataAtual.toLocaleString('pt-br', { month: 'long'}));
    this.anoTexto = dataAtual.getFullYear();
  }

  private primeiraLetraMaiuscula(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  buscarCompromissosDiario(dataHoje: Date) {
    const hoje = dataHoje.toISOString().split('T')[0];
    this.tarefaServico.buscarCompromissosDiarioAgrupadoPorHora(hoje).subscribe(resultado => {
      if (resultado.sucesso) {
        this.lista = resultado.dados;
        this.carregarTarefas(this.lista); // <-- Adicione isso aqui
      }
      this.loading = false;
    }, error => {
      this.toastr.error('Erro ao carregar compromissos');
      this.loading = false;
    });
  }
}
